// VH-67: Health Report ISO-week range picker.
// Replaces the legacy single-button "Generate Weekly Report" entry point.
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/outline'
import {
  compareIsoWeek,
  isoWeeksBetween,
  isoWeeksInYear,
  previousIsoWeek,
} from './isoWeek'

const MAX_RANGE_WEEKS = 12

function clampInt(value, min, max) {
  const n = Number.parseInt(value, 10)
  if (Number.isNaN(n)) return min
  if (n < min) return min
  if (n > max) return max
  return n
}

export default function HealthReportPicker({ onGenerate, isPending, hasError }) {
  const { t } = useTranslation()

  const defaultWeek = useMemo(() => previousIsoWeek(), [])
  const currentYear = new Date().getUTCFullYear()

  const [fromYear, setFromYear] = useState(defaultWeek.year)
  const [fromWeek, setFromWeek] = useState(defaultWeek.week)
  const [toYear, setToYear] = useState(defaultWeek.year)
  const [toWeek, setToWeek] = useState(defaultWeek.week)

  const validation = useMemo(() => {
    // Year sanity bound: current year - 5 to current year (matches backend).
    if (fromYear < currentYear - 5 || fromYear > currentYear ||
        toYear < currentYear - 5 || toYear > currentYear) {
      return { ok: false, message: t('reports.picker.validation.yearOutOfRange') }
    }
    if (fromWeek < 1 || fromWeek > isoWeeksInYear(fromYear) ||
        toWeek < 1 || toWeek > isoWeeksInYear(toYear)) {
      return { ok: false, message: t('reports.picker.validation.weekOutOfRange') }
    }
    if (compareIsoWeek(fromYear, fromWeek, toYear, toWeek) > 0) {
      return { ok: false, message: t('reports.picker.validation.fromAfterTo') }
    }
    if (isoWeeksBetween(fromYear, fromWeek, toYear, toWeek) > MAX_RANGE_WEEKS) {
      return { ok: false, message: t('reports.picker.validation.rangeTooWide') }
    }
    return { ok: true, message: '' }
  }, [fromYear, fromWeek, toYear, toWeek, currentYear, t])

  const disabled = !validation.ok || isPending

  const handleSubmit = (e) => {
    e.preventDefault()
    if (disabled) return
    onGenerate({
      start_year: fromYear,
      start_week: fromWeek,
      end_year: toYear,
      end_week: toWeek,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="card mb-6">
      <div className="card-body">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('reports.picker.title')}</h2>
          <p className="text-sm text-gray-500">{t('reports.picker.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* From week */}
          <div className="border rounded-lg p-3">
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t('reports.picker.fromWeek')}
            </div>
            <div className="flex gap-2">
              <label className="flex-1">
                <span className="block text-xs text-gray-500 mb-1">{t('reports.picker.year')}</span>
                <input
                  type="number"
                  className="input w-full"
                  value={fromYear}
                  min={currentYear - 5}
                  max={currentYear}
                  onChange={(e) => setFromYear(clampInt(e.target.value, currentYear - 5, currentYear))}
                />
              </label>
              <label className="flex-1">
                <span className="block text-xs text-gray-500 mb-1">{t('reports.picker.week')}</span>
                <input
                  type="number"
                  className="input w-full"
                  value={fromWeek}
                  min={1}
                  max={53}
                  onChange={(e) => setFromWeek(clampInt(e.target.value, 1, 53))}
                />
              </label>
            </div>
          </div>

          {/* To week */}
          <div className="border rounded-lg p-3">
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t('reports.picker.toWeek')}
            </div>
            <div className="flex gap-2">
              <label className="flex-1">
                <span className="block text-xs text-gray-500 mb-1">{t('reports.picker.year')}</span>
                <input
                  type="number"
                  className="input w-full"
                  value={toYear}
                  min={currentYear - 5}
                  max={currentYear}
                  onChange={(e) => setToYear(clampInt(e.target.value, currentYear - 5, currentYear))}
                />
              </label>
              <label className="flex-1">
                <span className="block text-xs text-gray-500 mb-1">{t('reports.picker.week')}</span>
                <input
                  type="number"
                  className="input w-full"
                  value={toWeek}
                  min={1}
                  max={53}
                  onChange={(e) => setToWeek(clampInt(e.target.value, 1, 53))}
                />
              </label>
            </div>
          </div>
        </div>

        {!validation.ok && (
          <p className="mt-3 text-sm text-danger-700">{validation.message}</p>
        )}

        {hasError && validation.ok && (
          <p className="mt-3 text-sm text-danger-700">{t('reports.picker.error')}</p>
        )}

        <div className="mt-4 flex justify-end">
          <button type="submit" className="btn-primary flex items-center" disabled={disabled}>
            {isPending ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('reports.picker.generating')}
              </>
            ) : (
              <>
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('reports.picker.generate')}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  )
}
